import { Component, type ReactNode, type ErrorInfo } from 'react';
import type { ConnectAccount, ConnectState } from '@/hooks/use-connect-status';
import type { StripeConnectInstance } from '@stripe/connect-js';
import {
    ConnectPayments,
    ConnectPayouts,
    ConnectAccountOnboarding,
    ConnectNotificationBanner,
    ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
    CreditCard,
    CheckCircle2,
    Clock,
    AlertCircle,
    XCircle,
    RefreshCw,
    Building2,
    Wallet,
    ArrowRight,
    Banknote,
    ShieldCheck,
} from 'lucide-react';

// ── Shared small components ─────────────────────────────────────────

export function StatusCard({ label, enabled, description }: { label: string; enabled: boolean; description: string }) {
    return (
        <div className={`rounded-lg border p-4 ${enabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
            <div className="flex items-center gap-2">
                {enabled ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                    <XCircle className="h-4 w-4 text-slate-400" />
                )}
                <span className={`text-sm font-medium ${enabled ? 'text-emerald-800' : 'text-slate-500'}`}>
                    {label}
                </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
    );
}

export function LoadingCard({ message }: { message: string }) {
    return (
        <Card className="p-8">
            <div className="flex items-center justify-center gap-3">
                <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                <p className="text-slate-500">{message}</p>
            </div>
        </Card>
    );
}

// ── Stripe Error Boundary ───────────────────────────────────────────

interface StripeErrorBoundaryProps {
    children: ReactNode;
    fallbackMessage?: string;
}

interface StripeErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export class StripeErrorBoundary extends Component<StripeErrorBoundaryProps, StripeErrorBoundaryState> {
    state: StripeErrorBoundaryState = { hasError: false, error: null };

    static getDerivedStateFromError(error: Error): StripeErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('ConnectPanel:StripeErrorBoundary: component error', error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <Card className="p-6">
                    <div className="flex flex-col items-center gap-3 text-center">
                        <AlertCircle className="h-8 w-8 text-red-400" />
                        <p className="text-sm text-slate-600">
                            {this.props.fallbackMessage ?? 'Failed to load Stripe component. Please refresh the page.'}
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => this.setState({ hasError: false, error: null })}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                        </Button>
                    </div>
                </Card>
            );
        }
        return this.props.children;
    }
}

// ── Connect status badge ────────────────────────────────────────────

export function connectStatusBadge(account: ConnectAccount) {
    switch (account.onboarding_status) {
        case 'complete':
            return (
                <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Active
                </Badge>
            );
        case 'pending':
            return (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                </Badge>
            );
        case 'restricted':
            return (
                <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Restricted
                </Badge>
            );
        case 'rejected':
            return (
                <Badge variant="destructive">
                    <XCircle className="h-3 w-3 mr-1" />
                    Rejected
                </Badge>
            );
        default:
            return <Badge variant="outline">{account.onboarding_status}</Badge>;
    }
}

// ── ConnectPanel ────────────────────────────────────────────────────

export interface ConnectPanelProps {
    connectState: ConnectState;
    stripeConnectInstance: StripeConnectInstance | null;
    fetchConnectStatus: () => void;
    onSetUpPayments: () => void;
    onboarding: boolean;
    entityName: string;
    setupDescription: string;
    setupFootnote: string;
    canManage?: boolean;
    connectedChildren?: ReactNode;
}

export function ConnectPanel({
    connectState,
    stripeConnectInstance,
    fetchConnectStatus,
    onSetUpPayments,
    onboarding,
    entityName,
    setupDescription,
    setupFootnote,
    canManage = true,
    connectedChildren,
}: ConnectPanelProps) {
    return (
        <div className="space-y-6">
            {/* Notification Banner */}
            {stripeConnectInstance && (connectState.kind === 'connected' || connectState.kind === 'onboarding') && (
                <StripeErrorBoundary fallbackMessage="Unable to load notifications.">
                    <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                        <ConnectNotificationBanner />
                    </ConnectComponentsProvider>
                </StripeErrorBoundary>
            )}

            {/* Loading */}
            {connectState.kind === 'loading' && (
                <Card className="border-white/30 bg-white/40 p-8 shadow-sm">
                    <div className="flex items-center justify-center gap-3">
                        <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
                        <p className="text-slate-500">Loading billing status...</p>
                    </div>
                </Card>
            )}

            {/* Error */}
            {connectState.kind === 'error' && (
                <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-400 bg-[hsl(var(--list-controls-empty-bg))]">
                    <div className="flex max-w-md flex-col items-center gap-3 px-4 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-50">
                            <AlertCircle size={24} className="text-red-400" />
                        </div>
                        <p className="text-sm text-[hsl(var(--list-controls-foreground))]">
                            {connectState.message}
                        </p>
                        <p className="text-sm text-[hsl(var(--list-controls-muted-foreground))]">
                            The billing service may be unavailable. Please try again later.
                        </p>
                        <Button variant="outline" onClick={fetchConnectStatus}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                        </Button>
                    </div>
                </div>
            )}

            {/* Not Connected */}
            {connectState.kind === 'not_connected' && (
                <Card className="border-white/30 bg-white/40 p-8 shadow-sm">
                    <div className="flex flex-col items-center gap-6 text-center max-w-md mx-auto">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-violet-50 to-indigo-50 border border-violet-100">
                            <CreditCard size={32} className="text-violet-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900">Connect Stripe to get paid</h2>
                            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                {setupDescription}
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 w-full">
                            {canManage ? (
                                <Button
                                    size="lg"
                                    onClick={onSetUpPayments}
                                    disabled={onboarding}
                                    className="w-full"
                                >
                                    {onboarding ? (
                                        <>
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            Setting up...
                                        </>
                                    ) : (
                                        <>
                                            Set Up Payments
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            ) : (
                                <p className="text-sm text-slate-500">
                                    An organization admin must connect Stripe before payouts can be enabled.
                                </p>
                            )}
                            <p className="text-xs text-slate-400">
                                {setupFootnote}
                            </p>
                        </div>
                    </div>
                </Card>
            )}

            {/* Onboarding */}
            {connectState.kind === 'onboarding' && stripeConnectInstance && (
                <Card className="border-white/30 bg-white/40 shadow-sm">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 border border-amber-200">
                                <Clock size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">Complete Your Account Setup</CardTitle>
                                <CardDescription>
                                    Fill in the details below to finish connecting your Stripe account and start receiving payouts.
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <StripeErrorBoundary fallbackMessage="Failed to load Stripe onboarding. Please refresh.">
                            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                                <ConnectAccountOnboarding onExit={() => fetchConnectStatus()} />
                            </ConnectComponentsProvider>
                        </StripeErrorBoundary>
                    </CardContent>
                </Card>
            )}

            {/* Connected */}
            {connectState.kind === 'connected' && (
                <div className="space-y-6 **:data-[slot=card]:border-slate-200 **:data-[slot=card]:dark:border-slate-700">
                    {/* Status Header */}
                    <Card className="border-white/30 bg-white/40 shadow-sm">
                        <CardHeader>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                                        <Building2 size={24} className="text-slate-600" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <CardTitle className="text-lg">Stripe Connect</CardTitle>
                                            {connectStatusBadge(connectState.account)}
                                        </div>
                                        <CardDescription className="mt-1">
                                            Account {connectState.account.stripe_account_id}
                                        </CardDescription>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" onClick={fetchConnectStatus}>
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    Refresh
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <StatusCard
                                    label="Charges"
                                    enabled={connectState.account.charges_enabled}
                                    description="Can accept payments"
                                />
                                <StatusCard
                                    label="Payouts"
                                    enabled={connectState.account.payouts_enabled}
                                    description="Can receive transfers"
                                />
                                <StatusCard
                                    label="Details Submitted"
                                    enabled={connectState.account.details_submitted}
                                    description="Identity verified"
                                />
                            </div>

                            {connectState.account.onboarding_status === 'pending' && (
                                <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                    <div className="flex items-start gap-3">
                                        <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-amber-800">
                                                Onboarding incomplete
                                            </p>
                                            <p className="text-sm text-amber-700 mt-1">
                                                Complete your Stripe onboarding to start receiving payouts.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {connectState.account.onboarding_status === 'restricted' && (
                                <div className="mt-6 rounded-lg border border-orange-200 bg-orange-50 p-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-orange-800">
                                                Account restricted
                                            </p>
                                            <p className="text-sm text-orange-700 mt-1">
                                                Your Stripe account has restrictions. Additional information may be required.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Separator />

                    {/* Tabbed Content */}
                    <div className="list-controls-surface list-controls-surface--solid">
                        <Tabs defaultValue="overview">
                            <TabsList>
                                <TabsTrigger value="overview">
                                    <ShieldCheck className="h-4 w-4 mr-1.5" />
                                    Overview
                                </TabsTrigger>
                                <TabsTrigger value="payments">
                                    <Banknote className="h-4 w-4 mr-1.5" />
                                    Payments
                                </TabsTrigger>
                                <TabsTrigger value="payouts">
                                    <Wallet className="h-4 w-4 mr-1.5" />
                                    Payouts
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="overview">
                                <div className="space-y-4">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Payout Information</CardTitle>
                                            <CardDescription>
                                                How earnings for {entityName} are calculated and distributed.
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="text-sm text-slate-600 space-y-3">
                                                <p>
                                                    Payouts are processed monthly. Provider settlement entries are aggregated
                                                    and transferred to the Stripe account on the 1st of each month.
                                                </p>
                                                <p>
                                                    Minimum payout threshold: <span className="font-medium">$10.00</span>.
                                                    Amounts below the threshold are carried forward to the next period.
                                                </p>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {connectedChildren}
                                </div>
                            </TabsContent>

                            <TabsContent value="payments">
                                {stripeConnectInstance ? (
                                    <StripeErrorBoundary fallbackMessage="Failed to load payments component.">
                                        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Payments</CardTitle>
                                                    <CardDescription>
                                                        View and manage incoming payments from marketplace customers.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent>
                                                    <ConnectPayments />
                                                </CardContent>
                                            </Card>
                                        </ConnectComponentsProvider>
                                    </StripeErrorBoundary>
                                ) : (
                                    <LoadingCard message="Initializing payments..." />
                                )}
                            </TabsContent>

                            <TabsContent value="payouts">
                                {stripeConnectInstance ? (
                                    <StripeErrorBoundary fallbackMessage="Failed to load payouts component.">
                                        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Payouts</CardTitle>
                                                    <CardDescription>
                                                        Balance overview, payout history, and bank account details.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent>
                                                    <ConnectPayouts />
                                                </CardContent>
                                            </Card>
                                        </ConnectComponentsProvider>
                                    </StripeErrorBoundary>
                                ) : (
                                    <LoadingCard message="Initializing payouts..." />
                                )}
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            )}
        </div>
    );
}
